import { cloneDeep, random } from 'lodash';
import { v4 as uuid } from 'uuid';
import { traverseTrees } from '@meitu/saas-editor-core/es/utils/tree-kit';
import { ILayerProps, LayerType } from '@meitu/saas-editor-core/es/version-types';
import { SmartComponentType } from '@/editor/panel/views/ppt-mark-panel/tag-list-config';
import { transferTemplateToClient } from '@/hooks/fetch-template/server-to-client';
import { fetchAIPPTMaterialDetail, fetchAIPPTMaterialDetailMultiV2 } from '@/services/ai';
import { PPTTemplate, randomMoveLayerPosition } from '../apply-ppt-template/apply-ppt-template';
import { graphDataMap, smartComponentContentJson } from '../apply-ppt-template/smart-component-content-json';
import gptContent from './gpt-content';

export const adjustScale = (setScale: (scale: number) => void) => {
    const container = document.body;
    const items = document.querySelectorAll('.render-stage');
    const gap = 16; // 子元素间距
    const padding = 16; // 容器内边距

    // 计算每行可容纳的子元素数量
    const containerWidth = container.clientWidth - padding * 2; // 容器可用宽度
    const itemWidth = 1920; // 子元素原始宽度
    const itemsPerRow = 3; // 每行放置三个子元素

    // 计算缩放比例
    const totalGap = gap * (itemsPerRow - 1); // 总间距
    const availableWidth = containerWidth - totalGap; // 可用宽度
    const scale = availableWidth / (itemWidth * itemsPerRow); // 缩放比例

    // 应用缩放
    items.forEach((item) => {
        (item as HTMLElement).style.transform = `scale(${scale})`;
        (item as HTMLElement).style.transformOrigin = 'left top';
        (item as HTMLElement).style.width = `${1920}px`;
        (item as HTMLElement).style.height = `${1080}px`;

        ((item as HTMLElement).parentElement as HTMLElement).style.width = `${availableWidth / 3}px`;

        ((item as HTMLElement).parentElement as HTMLElement).style.height = `${(availableWidth / 3 / 1920) * 1080}px`;
    });
    setScale(scale);
};

export const getRandomLists = (list: any[], count: number) => {
    const randomList = list.sort(() => Math.random() - 0.5).slice(0, count);
    return randomList;
};

export const getLayersArray = (layers: ILayerProps[]) => {
    const layerArray: ILayerProps[] = [];
    getAllLayer(layers, layerArray);

    return layerArray;
};

export const getAllLayer = (item: any[], total: any[]) => {
    for (let i = 0; i < item.length; i++) {
        total.push(item[i]);

        if (item[i] && item[i].children && item[i].children.length > 0) {
            getAllLayer(item[i].children as any[], total);
        }
    }
};

export const changeId = (layers: ILayerProps[]) => {
    traverseTrees(layers, (layer: ILayerProps) => {
        layer.id = uuid();
        if (layer.children && layer.children.length > 0) {
            layer.children.forEach((child) => {
                child.parentId = layer.id;
            });
        }
    });

    return layers;
};

export const getClientData = async (fonts: any[], templateData: any) => {
    const clientData = await transferTemplateToClient(JSON.parse(templateData.formula), fonts, '', false, true);

    return clientData.template;
};
export const getSmartComponentData = async (
    templateList: any[],
    componentList: any[],
    smartComponentId: string,
    fonts: any[],
) => {
    const smartComponentDetails = (await fetchAIPPTMaterialDetailMultiV2(smartComponentId)).data.data;
    const smartComponentDetail = smartComponentDetails[0];

    const componentIds = componentList
        .filter(
            (component) =>
                component.smart_type &&
                smartComponentDetail.layout &&
                component.smart_type.toString() === smartComponentDetail.layout.toString(),
        )
        .map((item) => item.id);
    if (componentIds.length === 0) {
        return [] as ILayerProps[][];
    }
    const componentDetails = (await fetchAIPPTMaterialDetailMultiV2(componentIds.join(','))).data.data;

    const smartComponentTemplate = await getClientData(fonts, smartComponentDetail);

    const transferToClient = async (templateData: any) => {
        const data = await transferTemplateToClient(templateData, fonts, '', false);
        return data.template.templateConf;
    };

    const allStageLayers = [] as ILayerProps[][];
    for (let i = 0; i < componentDetails.length; i++) {
        const componentDetail = componentDetails[i];

        let templateData;
        const templateDataId = templateList[random(0, templateList.length - 1)]?.id;

        if (templateDataId) {
            const templateDataRsp = (await fetchAIPPTMaterialDetail(templateDataId)).data.data;
            templateData = await getClientData(fonts, templateDataRsp);
        }

        for (let j = 0; j < smartComponentTemplate.templateConf.length; j++) {
            const pptContent = await PPTTemplate.getPPTContentDataByComponents(
                cloneDeep(gptContent as any),
                transferToClient,
                [componentDetail],
            );

            if (!pptContent || pptContent.length === 0) {
                continue;
            }

            pptContent[0].graph_components = [] as any;
            const smartComponentTemplateConf = cloneDeep(smartComponentTemplate.templateConf[j]);
            pptContent[0].graph_components[0] = cloneDeep(smartComponentContentJson.graph_components[0]);

            pptContent[0].graph_components[0].graph_component_point =
                smartComponentTemplateConf?.extraInfo?.componentPoint || 1;
            const componentType = smartComponentDetail.component_type as SmartComponentType;
            pptContent[0].graph_components[0].graph_data = cloneDeep((graphDataMap as any)[componentType]);

            const resultComponentData = cloneDeep(pptContent[0].componentData);
            await PPTTemplate.insertSmartComponentToComponent(
                resultComponentData.templateConf,
                cloneDeep([smartComponentTemplateConf]),
                cloneDeep(pptContent[0]),
            );
            pptContent[0].componentData = resultComponentData;
            const templateConf = PPTTemplate.generate({
                pptContents: cloneDeep(pptContent),
                templateData: cloneDeep(templateData!),
            });

            if (templateConf.length > 0) {
                // 如果是左右结构的，则需要将图片层移动到左侧
                if (smartComponentDetail.layout.toString() === '1') {
                    const canMoveLayers = [] as ILayerProps[];
                    traverseTrees(templateConf[0].layers, (layer: ILayerProps) => {
                        if (layer.layerType === LayerType.GROUP_LAYER && layer.extraInfo?.label?.moveable) {
                            canMoveLayers.push(layer);
                        }
                    });

                    randomMoveLayerPosition(canMoveLayers);
                }
                allStageLayers.push(changeId(cloneDeep(templateConf[0].layers)));
            }
        }
    }

    return allStageLayers;
};
